// Enums
export enum UserRole {
    USER = 'USER',
    PARENT = 'PARENT',
    ADMIN = 'ADMIN',
    MEDIATOR = 'MEDIATOR',
}

export enum TeamRole {
    CEO = 'CEO',
    COO = 'COO',
    CTO = 'CTO',
    CFO = 'CFO',
    SeniorConsultant = 'Senior Consultant',
    SpecialistConsultant = 'Specialist Consultant',
    JuniorConsultant = 'Junior Consultant',
    HR = 'HR',
    Marketing = 'Marketing',
    Sales = 'Sales',
    Support = 'Support',
    Intern = 'Intern',
}

export enum KnowledgeHubCategory {
    MarketAndEconomy = 'Market & Economy',
    InfrastructureAndOperations = 'Infrastructure & Operations',
    LegalAndTaxes = 'Legal & Taxes',
    WorkforceAndProductivity = 'Workforce & Productivity',
    QualityAndCompliance = 'Quality & Compliance',
    RegionalOpportunities = 'Regional Opportunities',
    GermanyAndEU = 'Germany & EU',
}

export enum KnowledgeHubCategoryDe {
    MarketAndEconomy = 'Markt & Wirtschaft',
    InfrastructureAndOperations = 'Infrastruktur & Standort',
    LegalAndTaxes = 'Recht & Steuern',
    WorkforceAndProductivity = 'Personal & Produktivität',
    QualityAndCompliance = 'Qualität & Compliance',
    RegionalOpportunities = 'Regionale Chancen',
    GermanyAndEU = 'Deutschland & EU',
}

// Pagination
export interface PaginationMeta {
    page: number;
    page_size: number;
    total: number;
    pages: number;
}

export interface PaginatedResponse<T> {
    items: T[];
    meta: PaginationMeta;
}

export interface AdminPageMeta {
    page: number;
    size: number;
    total: number;
    pages: number;
}

export interface AdminPageResponse<T> {
    items: T[];
    meta: AdminPageMeta;
}

// User
export interface User {
    user_id: string;
    name: string;
    email: string;
    user_name?: string;
    phone_number?: string;
    date_of_birth?: string;
    roles: UserRole[];
    profile_picture?: string;
    created_at: string;
    updated_at?: string;
    is_banned?: boolean;
    is_active?: boolean;
    is_deleted?: boolean;
}

export interface UserCreate {
    name: string;
    email: string;
    user_name: string;
    password: string;
    phone_number?: string;
    recaptcha_token?: string;
}

export interface UserUpdateAdmin {
    name?: string;
    phone_number?: string;
    email?: string;
    user_name?: string;
    roles?: UserRole[];
}

export interface StaffAccountCreate {
    name: string;
    email: string;
    user_name: string;
    password?: string;
    phone_number?: string;
    roles: UserRole[];
    recaptcha_token?: string;
}

// Auth
export interface LoginCredentials {
    username: string;
    password: string;
}

export interface AuthTokens {
    access_token: string;
    token_type: string;
}

export interface PasswordChange {
    old_password: string;
    new_password: string;
    confirm_password: string;
}

export interface UserProfileUpdate {
    name?: string;
    phone_number?: string;
    date_of_birth?: string;
}

// Blog
export interface BlogSlim {
    id: string;
    slug: string;
    title: string;
    title_de?: string;
    abstract?: string;
    abstract_de?: string;
    Author?: string;
    Author_de?: string;
    image?: string;
    image_description?: string;
    image_description_de?: string;
    created_at?: string;
    is_draft: boolean;
    is_active: boolean;
}

export interface Blog extends BlogSlim {
    author_image?: string;
    body: string;
    body_de?: string;
    updated_at?: string;
}

export interface BlogAdmin extends Blog {
    is_deleted: boolean;
    created_by: string;
}

export interface BlogCreate {
    title: string;
    title_de?: string;
    abstract?: string;
    abstract_de?: string;
    Author?: string;
    Author_de?: string;
    body: string;
    body_de?: string;
    image_description?: string;
    image_description_de?: string;
    is_draft?: boolean;
    is_active?: boolean;
}

export interface BlogUpdate {
    title?: string;
    title_de?: string;
    abstract?: string;
    abstract_de?: string;
    Author?: string;
    Author_de?: string;
    body?: string;
    body_de?: string;
    image_description?: string;
    image_description_de?: string;
    is_draft?: boolean;
    is_active?: boolean;
}

export interface BlogAdminStatusUpdate {
    is_deleted?: boolean;
    is_active?: boolean;
    is_draft?: boolean;
}

// Knowledge Hub
export interface KnowledgeHubSlim {
    id: string;
    title: string;
    title_de?: string;
    abstract?: string;
    abstract_de?: string;
    topic?: string;
    topic_de?: string;
    category?: KnowledgeHubCategory;
    category_de?: KnowledgeHubCategoryDe;
    image?: string;
    image_description?: string;
    image_description_de?: string;
    created_at?: string;
    is_draft: boolean;
    is_active: boolean;
}

export interface KnowledgeHub extends KnowledgeHubSlim {
    body: string;
    body_de?: string;
    updated_at?: string;
}

export interface KnowledgeHubAdmin extends KnowledgeHub {
    is_deleted: boolean;
    created_by: string;
}

export interface KnowledgeHubCreate {
    title: string;
    title_de?: string;
    abstract?: string;
    abstract_de?: string;
    body: string;
    body_de?: string;
    image_description?: string;
    image_description_de?: string;
    topic?: string;
    topic_de?: string;
    category?: KnowledgeHubCategory;
    category_de?: KnowledgeHubCategoryDe;
    is_draft?: boolean;
    is_active?: boolean;
}

export interface KnowledgeHubUpdate {
    title?: string;
    title_de?: string;
    abstract?: string;
    abstract_de?: string;
    body?: string;
    body_de?: string;
    image_description?: string;
    image_description_de?: string;
    topic?: string;
    topic_de?: string;
    category?: KnowledgeHubCategory;
    category_de?: KnowledgeHubCategoryDe;
    is_draft?: boolean;
    is_active?: boolean;
}

export interface KnowledgeHubAdminStatusUpdate {
    is_deleted?: boolean;
    is_active?: boolean;
    is_draft?: boolean;
}

// Team
export interface TeamMemberSlim {
    id: string;
    name: string;
    name_de?: string;
    role?: TeamRole;
    image?: string;
    linkedin?: string;
    created_at?: string;
    is_active: boolean;
}

export interface TeamMember extends TeamMemberSlim {
    info?: string;
    info_de?: string;
    updated_at?: string;
}

export interface TeamMemberAdmin extends TeamMember {
    is_deleted: boolean;
    created_by: string;
}

export interface TeamMemberCreate {
    name: string;
    name_de?: string;
    info?: string;
    info_de?: string;
    role?: TeamRole;
    linkedin?: string;
    is_active?: boolean;
}

export interface TeamMemberUpdate {
    name?: string;
    name_de?: string;
    info?: string;
    info_de?: string;
    role?: TeamRole;
    linkedin?: string;
    is_active?: boolean;
}

export interface TeamMemberAdminStatusUpdate {
    is_deleted?: boolean;
    is_active?: boolean;
}

// Newsletter
export interface NewsletterSubscriptionAdmin {
    id: string;
    email: string;
    name: string;
    language?: string;
    created_at?: string;
    updated_at?: string;
    is_deleted: boolean;
    is_active: boolean;
}

export interface NewsletterCreate {
    subject: string;
    body: string;
    subject_de?: string;
    body_de?: string;
    link_url?: string;
    link_label?: string;
}

export interface NewsletterRead {
    id: string;
    subject: string;
    subject_de?: string;
    body: string;
    body_de?: string;
    status?: string;
    sent_count?: number;
    failed_count?: number;
    created_at?: string;
    updated_at?: string;
    is_deleted: boolean;
    is_active: boolean;
    created_by: string;
}

export interface NewsletterListItem {
    id: string;
    subject: string;
    status?: string;
    sent_count?: number;
    failed_count?: number;
    created_at?: string;
    is_active: boolean;
}

// Contact Us
export interface ContactSubmission {
    id: string;
    name: string;
    email: string;
    title: string;
    phone_number: string;
    message: string;
    created_at?: string;
    updated_at?: string;
    is_read?: boolean;
    is_resolved?: boolean;
    resolved?: boolean;
}

export interface ContactSubmissionCreate {
    name: string;
    email: string;
    title: string;
    phone_number: string;
    message: string;
}

export interface DashboardStats {
    users: number;
    knowledge_hub: number;
    blog: number;
    team: number;
    newsletter_subscriptions: number;
}
